import type {Node, Edge} from '@xyflow/react';
import type {GraphLabel} from '@dagrejs/dagre';
import {graphlib, layout} from '@dagrejs/dagre';
import {TreeNode} from '@/types';

// Card dimensions
const CARD_WIDTH = 432;
const CARD_HEIGHT = 200;
const SURGERY_CARD_WIDTH = 450;
const SURGERY_CARD_HEIGHT = 400;
const PLATFORM_CARD_WIDTH = 450;
const PLATFORM_CARD_HEIGHT = 250;
const TOP100KA_CARD_WIDTH = 450;
const TOP100KA_CARD_HEIGHT = 210;
const CATEGORY_CARD_WIDTH = 360;
const CATEGORY_CARD_HEIGHT = 150;

function getCardDimensions(node: TreeNode, isCategory: boolean) {
    const nodeId = String(node.id).toLowerCase();
    if (nodeId === 'top100ka') return {width: TOP100KA_CARD_WIDTH, height: TOP100KA_CARD_HEIGHT};
    if (isCategory) return {width: CATEGORY_CARD_WIDTH, height: CATEGORY_CARD_HEIGHT};
    const nodeType = String(node.type || '').toLowerCase();
    if (nodeId === 'root' || nodeType === 'surgery') return {width: SURGERY_CARD_WIDTH, height: SURGERY_CARD_HEIGHT};
    if (nodeType === 'platform' || /^(mip|ae|bio|wch)$/.test(nodeId)) return {
        width: PLATFORM_CARD_WIDTH,
        height: PLATFORM_CARD_HEIGHT
    };
    return {width: CARD_WIDTH, height: CARD_HEIGHT};
}

function isCategoryNode(node: TreeNode): boolean {
    return !node.status && !!node.children && node.children.length > 0;
}

function isNodeCritical(node: TreeNode): boolean {
    return node.status === 'critical';
}

function isPlatformNode(node: TreeNode): boolean {
    const type = String(node.type || '').toLowerCase();
    const id = String(node.id || '').toLowerCase();
    return type === 'platform' || /^(mip|ae|bio|wch)$/.test(id);
}

function isRegionCategoryNode(node: TreeNode): boolean {
    const id = String(node.id || '').toLowerCase();
    const type = String(node.type || '').toLowerCase();
    return type === 'category' && id.includes('-region');
}

function getNodeGapValue(node: TreeNode): number | null {
    const mappedValue = node.metrics_map?.ims_amt_mtd_gap?.value;
    if (mappedValue != null) return mappedValue;

    const ims_nonvbp_amt_mtd_gap = node.metrics_map?.ims_nonvbp_amt_mtd_gap?.value;
    if (ims_nonvbp_amt_mtd_gap != null) return ims_nonvbp_amt_mtd_gap;
    const ims_vbp_amt_mtd_gap = node.metrics_map?.ims_vbp_amt_mtd_gap?.value;
    if (ims_vbp_amt_mtd_gap != null) return ims_vbp_amt_mtd_gap;

    return null;
}

export function findHighlightPath(root: TreeNode): Set<string> {
    const path = new Set<string>([root.id]);
    const platforms = (root.children ?? []).filter(isPlatformNode);

    let selectedPlatform: TreeNode | null = null;
    let smallestGap = Infinity;
    for (const platform of platforms) {
        const gap = getNodeGapValue(platform);
        if (gap !== null && gap < smallestGap) {
            smallestGap = gap;
            selectedPlatform = platform;
        }
    }

    if (!selectedPlatform) return path;
    path.add(selectedPlatform.id);

    const regionCategory = selectedPlatform.children?.find(isRegionCategoryNode);
    if (regionCategory) path.add(regionCategory.id);

    return path;
}

function hasCriticalDescendant(node: TreeNode): boolean {
    if (isNodeCritical(node)) return true;
    if (node.children) return node.children.some(hasCriticalDescendant);
    return false;
}

export function treeToFlow(
    root: TreeNode,
    expandedNodes: Set<string>,
    filterCritical: boolean,
    highlightPath: Set<string> | null,
    batchId: string
): { nodes: Node[]; edges: Edge[] } {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    const processNode = (node: TreeNode, parentNode: TreeNode | null = null) => {

        if (node.id == "top100ka_surgery") {
            // 这个数据不需要，也不存在，后端算多余的，这里直接屏蔽就好，top100ka的数据其实就是top100ka_surgery的
            return
        }

        const isCategory = isCategoryNode(node);
        const isProvinceCategory = node.id?.endsWith('-province') || node.id?.endsWith('-segment');
        const isProvinceOrSegment =
            !isProvinceCategory &&
            (node.type === 'Province' || node.type === 'Segment' ||
                node.type?.toLowerCase() === 'province' || node.type?.toLowerCase() === 'segment' ||
                (node.id?.includes('-province-') || node.id?.includes('-segment-')));
        const isTop100KA = node.id === 'top100ka';
        const isGrowthPlan = node.id?.endsWith('-growth-plan');
        const isVbpProvince = node.id?.includes('-vbp-province-') || node.id?.includes('-nonvbp-province-');
        const needsHospitalList = ((isProvinceOrSegment && !isVbpProvince) || isTop100KA || isGrowthPlan);
        const hasRealChildren = node.children && node.children.length > 0;
        const hasChildren = hasRealChildren || needsHospitalList;

        if (isProvinceOrSegment) {
            if (!node.metrics_map?.['ims_amt_mtd_gap'] || node.metrics_map?.['ims_amt_mtd_gap'].value == 0) {
                if (!node.metrics_map?.['ims_vbp_amt_mtd_gap'] || node.metrics_map?.['ims_vbp_amt_mtd_gap'].value == 0) {
                    if (!node.metrics_map?.['ims_nonvbp_amt_mtd_gap'] || node.metrics_map?.['ims_nonvbp_amt_mtd_gap'].value == 0) {
                        return;
                    }
                }
            }
        }


        nodes.push({
            id: node.id,
            type: node.type,
            position: {x: 0, y: 0},
            data: {node, parentNode, expanded: expandedNodes.has(node.id), hasChildren, isCategory},
        });

        if (parentNode) {
            edges.push({
                id: `${parentNode.id}->${node.id}`,
                source: parentNode.id,
                target: node.id,
                type: 'default',
            });
        }

        if (expandedNodes.has(node.id)) {
            if (needsHospitalList) {
                const hospitalListNodeId = `${node.id}-hospital-list`;
                nodes.push({
                    id: hospitalListNodeId,
                    type: 'hospitalList',
                    position: {x: 0, y: 0},
                    data: {node, batch: batchId},
                });
                edges.push({
                    id: `${node.id}->${hospitalListNodeId}`,
                    source: node.id,
                    target: hospitalListNodeId,
                    type: 'default',
                    style: {stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '5,5'},
                });
            }

            if (hasRealChildren) {
                let children = node.children!.filter(
                    (child) => !(child.id && child.id.endsWith('-products'))
                );

                if (highlightPath && highlightPath.size > 0) {
                    if (node.id !== 'root') {
                        const hasNextPathNode = children.some((child) => highlightPath.has(child.id));
                        // const isPathEnd = highlightPath.has(node.id) && !hasNextPathNode;
                        children = children.filter((child) => {
                            const gap = getNodeGapValue(child);
                            return highlightPath.has(child.id) || child.type == "category" || (gap !== null && gap < 0) ;
                        });
                    }
                } else if (filterCritical) {
                    if (node.id === 'root') {
                        children = children.filter((child) => {
                            if (child.id === 'top100ka') return false;
                            if (child.type === 'Platform') return hasCriticalDescendant(child);
                            return true;
                        });
                    }
                }

                children.forEach((child) => processNode(child, node));
            }
        }
    };

    processNode(root);
    return {nodes, edges};
}

export function getLayoutedElements(
    nodes: Node[],
    edges: Edge[]
): { nodes: Node[]; edges: Edge[] } {
    const g = new graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({rankdir: 'LR', nodesep: 72, ranksep: 180, align: 'UL'} as GraphLabel);

    nodes.forEach((node) => {
        const data = node.data as any;
        const isCategory = data?.isCategory;
        const dims = getCardDimensions(data?.node, isCategory);
        g.setNode(node.id, {width: dims.width, height: dims.height});
    });

    edges.forEach((edge) => {
        g.setEdge(edge.source, edge.target);
    });

    layout(g);

    const layoutedNodes = nodes.map((node) => {
        const pos = g.node(node.id);
        const data = node.data as any;
        const isCategory = data?.isCategory;
        const dims = getCardDimensions(data?.node, isCategory);
        return {
            ...node,
            position: {x: pos.x - dims.width / 2, y: pos.y - dims.height / 2},
        };
    });

    const nodeById = new Map(layoutedNodes.map((n) => [n.id, n]));
    const rootNode = nodeById.get('root');
    if (rootNode) {
        const firstLevelChildIds = edges.filter((e) => e.source === 'root').map((e) => e.target);
        const firstLevelChildren = firstLevelChildIds.map((id) => nodeById.get(id)).filter(Boolean);
        if (firstLevelChildren.length > 0) {
            const childCenters = firstLevelChildren.map((child) => {
                const data = child!.data as any;
                const dims = getCardDimensions(data?.node, data?.isCategory);
                return child!.position.y + dims.height / 2;
            });
            const minCenter = Math.min(...childCenters);
            const maxCenter = Math.max(...childCenters);
            const targetCenterY = (minCenter + maxCenter) / 2;
            const rootData = rootNode.data as any;
            const rootDims = getCardDimensions(rootData?.node, rootData?.isCategory);
            rootNode.position.y = targetCenterY - rootDims.height / 2;
        }
    }

    return {nodes: layoutedNodes, edges};
}
